import React, { useState } from "react";
import { Line } from "react-chartjs-2";
import {
  faChartLine,
  faDatabase,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend
);

const DashboardTab = ({ userData }) => {
  const [chartPeriod, setChartPeriod] = useState("7");

  const getStorageLimit = (plan, planType) => {
    // Priority 1: Use the limit from the backend if available
    if (planType?.limit && planType.limit > 0) {
      return planType.limit;
    }

    // Priority 2: Fallback to hardcoded limits based on plan name
    switch (plan?.toLowerCase()) {
      case "monthly":
      case "yearly":
        return 5 * 1024; // 5 GB in MB
      case "free":
        return 60; // 60 MB for Free
      default:
        return 0; // Neutral state
    }
  };

  const usedStorage = (Number(userData?.totalStorageUsed) || 0) / (1024 * 1024);
  const storageLimit = getStorageLimit(userData?.plan, userData?.planType);
  const usedPercentage = storageLimit > 0 
    ? Math.min(Math.round((usedStorage / storageLimit) * 100), 100)
    : 0;

  const formatStorage = (mb) => {
    if (mb >= 1024) {
      return (mb / 1024).toFixed(1) + " GB";
    }
    return mb.toFixed(1) + " MB";
  };

  const views = userData.stats.viewsData[chartPeriod] || [];
  const chartData = {
    labels: views?.map((item) => item.date),
    datasets: [
      {
        label: "Profile Views",
        data: views?.map((item) => item.totalViews),
        borderColor: "#6c5ce7",
        backgroundColor: "rgba(108, 92, 231, 0.08)",
        tension: 0.35,
        fill: true,
        pointRadius: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { mode: "index", intersect: false },
    },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, grid: { color: "rgba(0,0,0,0.05)" } },
    },
  };

  return (
    <div>
      <h2 className="section-title">
        <FontAwesomeIcon icon={faChartLine} /> Profile Analytics
      </h2>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">
            {userData.stats.totalViews.toLocaleString()}
          </div>
          <div className="stat-label">Total Views</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {userData.stats.uniqueVisitors.toLocaleString()}
          </div>
          <div className="stat-label">Unique Visitors</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {userData.stats.bioscore.toLocaleString()}
          </div>
          <div className="stat-label">BioScore</div>
        </div>
      </div>

      <div className="time-filters">
        {["7", "30", "90"].map((p) => (
          <button
            key={p}
            className={`time-filter ${chartPeriod === p ? "active" : ""}`}
            onClick={() => setChartPeriod(p)}
          >
            Last {p} Days
          </button>
        ))}
      </div>

      <div className="chart-container" style={{ height: 320 }}>
        <Line data={chartData} options={chartOptions} />
      </div>

      {/* Storage Indicator */}
      <div className="storage-indicator-container">
        <div className="storage-header">
          <h3 className="storage-title">
            <FontAwesomeIcon icon={faDatabase} style={{ color: "#6c5ce7" }} />
            Storage Usage
          </h3>
          <span className="storage-percentage">{usedPercentage}% Used</span>
        </div>

        <div className="storage-progress-bar">
          <div
            className="storage-progress-fill"
            style={{ width: `${usedPercentage}%` }}
          ></div>
        </div>

        <div className="storage-footer">
          <div className="storage-info-item">
            <span className="storage-info-label">Used Space</span>
            <span className="storage-info-value">
              {formatStorage(usedStorage)}
            </span>
          </div>
          <div className="storage-info-item remaining-value">
            <span className="storage-info-label">Total Limit</span>
            <span className="storage-info-value">
              {formatStorage(storageLimit)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardTab;
